/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';

const messages = defineMessages({
    gotIt: { id: 'luis.conversationApplicationsAzurePicker.gotIt', defaultMessage: 'Got it' },
    subscriptionLabel: { id: 'luis.conversationApplicationsAzurePicker.subscriptionLabel', defaultMessage: 'Azure subscription:' },
    authoringResourceLabel: {
        id: 'luis.conversationApplicationsAzurePicker.authoringResourceLabel',
        defaultMessage: 'Authoring resource:'
    },
    subscriptionPlaceholder: {
        id: 'luis.conversationApplicationsAzurePicker.subscriptionPlaceholder',
        defaultMessage: 'No subscription selected'
    },
    authoringResourcePlaceholder: {
        id: 'luis.conversationApplicationsAzurePicker.authoringResourcePlaceholder',
        defaultMessage: 'No authoring resource selected'
    },
    chooseAuthoringResource: {
        id: 'luis.conversationApplicationsAzurePicker.chooseAuthoringResource',
        defaultMessage: 'Choose a different authoring resource.'
    },
    teachingBubbleTitle: {
        id: 'luis.conversationApplicationsAzurePicker.teachingBubbleTitle',
        defaultMessage: 'No authoring resource selected'
    },
    teachingBubbleBody: {
        id: 'luis.conversationApplicationsAzurePicker.teachingBubbleBody',
        defaultMessage: 'Click on choose a different authoring resource to pick one.'
    }
});

const stackStyles = classNamesFunction<IStackProps, IStackStyles>()({ root: { marginBottom: 15 } });
const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { fontSize: 12, height: 24 } });

export const AzurePickerBar = observer(() => {
    const { dispatcher, rootStore, uiStore } = useGlobalState();
    const { localize } = useLocalization();

    const azureSession = rootStore.azureStore.session;
    const selectedCsAccount = azureSession.csAccountStore;
    const subscriptionsHouse = rootStore.azureStore.subscriptionStoreHouse;
    const chooseAuthoringResourceId = React.useRef(getId('chooseAuthoringResourceId')).current;
    const [isTeachingBubbleHidden, setIsTeachingBubbleHidden] = React.useState(true);
    const selectedSubscription = subscriptionsHouse.items.find(item => item.id === azureSession.subscriptionId);

    React.useEffect(() => {
        // Displays the teaching bubble if there is no selected authoring resource for 15 sec
        // Once the authoring resource is selected through the modal
        // this component will be unmounted and the timer will be cleared
        if (!selectedSubscription && !selectedCsAccount) {
            const timeoutId = setTimeout(() => {
                setIsTeachingBubbleHidden(false);
            }, 15000);

            return () => clearTimeout(timeoutId);
        }
    }, [selectedSubscription, selectedCsAccount]);

    const shouldDisplayTeachingBubble =
        uiStore.isSwitchAccountModalHidden && !isTeachingBubbleHidden && !selectedSubscription && !selectedCsAccount;

    return (
        <Stack id="azurePickerBarRoot" horizontal verticalAlign="center" styles={stackStyles} tokens={{ childrenGap: 12 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <LuisText variant="smallPlus" fontWeight={600}>
                    {localize(messages.subscriptionLabel)}
                </LuisText>

                {subscriptionsHouse.tracker.isRunning ? (
                    <Shimmer width="100px" shimmerElements={[{ type: ShimmerElementType.line, height: 8 }]} />
                ) : (
                    <LuisText>{selectedSubscription?.name ?? localize(messages.subscriptionPlaceholder)}</LuisText>
                )}
            </Stack>

            <LuisText variant="smallPlus" fontWeight={600}>
                /
            </LuisText>

            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <LuisText variant="smallPlus" fontWeight={600}>
                    {localize(messages.authoringResourceLabel)}
                </LuisText>

                {subscriptionsHouse.tracker.isRunning ? (
                    <Shimmer width="100px" shimmerElements={[{ type: ShimmerElementType.line, height: 8 }]} />
                ) : (
                    <LuisText>{selectedCsAccount?.name ?? localize(messages.authoringResourcePlaceholder)}</LuisText>
                )}
            </Stack>

            <ActionButton
                styles={buttonStyles}
                id={chooseAuthoringResourceId}
                iconProps={{ iconName: 'EditSolid12' }}
                text={localize(messages.chooseAuthoringResource)}
                data-automation-id="chooseAnotherAuthoringResource"
                onClick={() => dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible: true })}
            />

            {shouldDisplayTeachingBubble && (
                <TeachingBubble
                    target={`#${chooseAuthoringResourceId}`}
                    headline={localize(messages.teachingBubbleTitle)}
                    onDismiss={() => setIsTeachingBubbleHidden(true)}
                    primaryButtonProps={{
                        children: localize(messages.gotIt),
                        onClick: () => setIsTeachingBubbleHidden(true)
                    }}>
                    {localize(messages.teachingBubbleBody)}
                </TeachingBubble>
            )}
        </Stack>
    );
});
